import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Search, 
  Trash, 
  Loader2,
  AlertCircle,
  Mail,
  Calendar,
  RefreshCw
} from "lucide-react";
import { format } from "date-fns";
import { api } from "@/lib/api";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";

const SubscribersAdmin = () => {
  const [search, setSearch] = useState("");
  const [deletingSubscriberId, setDeletingSubscriberId] = useState<string | null>(null);
  const { toast } = useToast();
  
  const queryClient = useQueryClient();
  
  const { 
    data: subscribers = [], 
    isLoading, 
    isError,
    error,
    refetch
  } = useQuery({
    queryKey: ['subscribers'],
    queryFn: api.subscribers.getAll,
    retry: 1,
    refetchOnWindowFocus: false,
    staleTime: 30000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
  
  // Add a timeout to prevent infinite loading
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (isLoading) {
        toast({
          title: "Проблема с загрузкой",
          description: "Загрузка подписчиков занимает больше времени, чем обычно. Пожалуйста, подождите или попробуйте обновить страницу.",
          variant: "destructive",
        });
      }
    }, 10000); // 10 seconds
    
    return () => clearTimeout(timeoutId);
  }, [isLoading, toast]);
  
  const filteredSubscribers = subscribers.filter((subscriber: any) =>
    subscriber.email.toLowerCase().includes(search.toLowerCase())
  );

  const handleDeleteSubscriber = async () => {
    if (deletingSubscriberId) {
      try {
        await api.subscribers.delete(deletingSubscriberId);
        setDeletingSubscriberId(null);
        queryClient.invalidateQueries({ queryKey: ['subscribers'] });
        toast({
          title: "Подписчик удален",
          description: "Подписчик был успешно удален из базы данных.",
        });
      } catch (error) {
        console.error('Error deleting subscriber:', error);
        toast({
          title: "Ошибка удаления",
          description: "Не удалось удалить подписчика. Пожалуйста, попробуйте позже.",
          variant: "destructive",
        });
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-destructive">
        <AlertCircle className="h-8 w-8 mb-4" />
        <p className="text-lg font-medium">Ошибка загрузки подписчиков</p>
        <p className="text-sm text-muted-foreground mt-2">
          {error instanceof Error ? error.message : 'Пожалуйста, попробуйте позже'}
        </p>
        <Button 
          variant="outline" 
          className="mt-4"
          onClick={() => refetch()}
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Попробовать снова
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Подписчики</h1>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => refetch()}
          disabled={isLoading}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Обновить
        </Button>
      </div>
      
      <div className="relative w-full max-w-sm">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          type="search"
          placeholder="Поиск подписчиков..."
          className="pl-8"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      
      {filteredSubscribers.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          Подписчиков не найдено
        </div>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Дата подписки</TableHead>
                <TableHead className="w-[100px]">Действия</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSubscribers.map((subscriber: any) => (
                <TableRow key={subscriber.id}>
                  <TableCell>
                    <div className="flex items-center">
                      <Mail className="h-4 w-4 mr-2 text-muted-foreground" />
                      <a href={`mailto:${subscriber.email}`} className="text-primary hover:underline">
                        {subscriber.email}
                      </a>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
                      {format(new Date(subscriber.created_at), 'dd.MM.yyyy HH:mm')}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeletingSubscriberId(subscriber.id)}
                    >
                      <Trash className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      
      <AlertDialog open={!!deletingSubscriberId} onOpenChange={() => setDeletingSubscriberId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить подписчика?</AlertDialogTitle>
            <AlertDialogDescription>
              Это действие нельзя отменить. Подписчик будет удален навсегда.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteSubscriber} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SubscribersAdmin; 